/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { useMediaQuery, useTheme } from "@mui/material";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDBadge from "components/MDBadge";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";
import { logoutUser } from "actions/AuthAction";

// Utils
import { API_ESA_OFFICE } from "utils";
import axios from "axios";
import swal from "sweetalert";

// Routes
import routes from "routes";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 3 PRO React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

function DashboardNavbar({
  absolute = false,
  light = false,
  isMini = false,
  avatar = null,
}) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    miniSidenav,
    transparentNavbar,
    fixedNavbar,
    openConfigurator,
    darkMode,
  } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [avatarMenu, setAvatarMenu] = useState(null);
  const route = useLocation().pathname.split("/").slice(1);
  const [userStatus, setUserStatus] = useState("");
  const [userAvatar, setUserAvatar] = useState(null);
  const [userId, setUserId] = useState(null);
  const [uid_RSS, setUid_RSS] = useState(null);
  const [notifications, setNotifications] = useState({
    disposisi: [],
    instruksi: [],
    events: [],
    laporan_harian: [],
    total: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchAnchorEl, setSearchAnchorEl] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [availableRoutes, setAvailableRoutes] = useState([]);
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [liburDialogOpen, setLiburDialogOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);

  // Filter routes berdasarkan user status
  useEffect(() => {
    const filterAvailableRoutes = (routesList, userStatus, levelJabatan) => {
      const checkRouteAvailability = (route, userStatus, levelJabatan) => {
        if (route.allowedStatuses && route.allowedStatuses.length > 0) {
          if (!route.allowedStatuses.includes(userStatus)) {
            return false;
          }
        }
        if (route.allowedLevelJabatan && route.allowedLevelJabatan.length > 0) {
          if (
            !levelJabatan ||
            !route.allowedLevelJabatan.includes(levelJabatan)
          ) {
            return false;
          }
        }
        return route.available !== false;
      };

      return routesList.filter((route) => {
        if (!checkRouteAvailability(route, userStatus, levelJabatan)) {
          return false;
        }
        if (route.collapse) {
          const filteredCollapse = filterAvailableRoutes(
            route.collapse,
            userStatus,
            levelJabatan
          );
          if (filteredCollapse.length === 0) {
            return false;
          }
          route.collapse = filteredCollapse;
        }
        return true;
      });
    };

    try {
      const u = localStorage.getItem("user");
      if (u) {
        const parsed = JSON.parse(u);
        const userStatus = parsed?.status || "";
        const levelJabatan = parsed?.level_jabatan || "";
        const filtered = filterAvailableRoutes(
          routes,
          userStatus,
          levelJabatan
        );
        setAvailableRoutes(filtered);
      } else {
        setAvailableRoutes([]);
      }
    } catch (e) {
      setAvailableRoutes([]);
    }
  }, []);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(
        dispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      );
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  useEffect(() => {
    try {
      const u = localStorage.getItem("user");
      if (u) {
        const parsed = JSON.parse(u);
        setUserStatus(parsed?.status || "");
        setUserId(parsed?.uid || parsed?.user_id || null);
        setUid_RSS(parsed?.uid_RSS || null);
        // Set avatar dari uid_RSS
        if (parsed?.uid_RSS) {
          setUserAvatar(
            `https://smartdh.rotendaokab.go.id/uploads/${parsed.uid_RSS}.jpg`
          );
        } else {
          setUserAvatar(null);
        }
      } else {
        setUserStatus("");
        setUserAvatar(null);
        setUserId(null);
      }
    } catch (e) {
      setUserStatus("");
      setUserAvatar(null);
      setUserId(null);
    }
  }, []);

  // Fetch notifications dengan Page Visibility API optimization
  useEffect(() => {
    if (!userId) return;

    let intervalId = null;

    const fetchNotifications = async () => {
      try {
        const response = await axios.get(
          `${API_ESA_OFFICE}users/${userId}/notifications`
        );
        setNotifications(response.data);
      } catch (error) {
        // Handle 403 error (user tidak aktif)
        if (error.response && error.response.status === 403) {
          const reason =
            error.response.data?.reason ||
            "Akun Anda tidak aktif. Silakan hubungi administrator.";
          swal({
            title: "Akses Ditolak",
            text: reason,
            icon: "warning",
            dangerMode: true,
            buttons: {
              confirm: {
                text: "OK",
                value: true,
              },
            },
          }).then(() => {
            // Clear local storage dan logout
            try {
              localStorage.removeItem("user");
              localStorage.removeItem("token");
              localStorage.removeItem("refreshToken");
            } catch (e) {
              console.error("Error clearing storage:", e);
            }
            logoutUser(navigate);
          });
          return;
        }
        console.error("Error fetching notifications:", error);
      }
    };

    // Handle visibility change
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;

      if (isVisible) {
        // Window kembali aktif - fetch notification sekali untuk update terbaru
        fetchNotifications();
        // Resume interval jika belum ada
        if (!intervalId) {
          intervalId = setInterval(fetchNotifications, 30 * 60 * 1000);
        }
      } else {
        // Window menjadi hidden - pause interval
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    };

    // Initial fetch
    fetchNotifications();

    // Setup interval hanya jika window visible saat ini
    if (!document.hidden) {
      intervalId = setInterval(fetchNotifications, 30 * 60 * 1000);
    }

    // Listen to visibility change events
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [userId, navigate]);

  // Helper function untuk mendapatkan data dari localStorage dengan error handling
  const getStoredAttendanceData = () => {
    try {
      const storedData = localStorage.getItem("daftarHadir");
      if (!storedData) return null;

      const parsed = JSON.parse(storedData);
      return parsed;
    } catch (error) {
      console.error("Error reading daftarHadir from localStorage:", error);
      // Hapus data yang corrupt
      try {
        localStorage.removeItem("daftarHadir");
      } catch (e) {
        console.error("Error removing corrupt daftarHadir:", e);
      }
      return null;
    }
  };

  // Helper function untuk menyimpan data ke localStorage dengan error handling
  const saveAttendanceDataToStorage = (data) => {
    const dataToStore = {
      ...data,
      lastFetched: new Date().toISOString(), // Timestamp kapan data terakhir di-fetch
    };

    try {
      localStorage.setItem("daftarHadir", JSON.stringify(dataToStore));
      return true;
    } catch (error) {
      // Handle quota exceeded atau error lainnya
      if (error.name === "QuotaExceededError") {
        console.warn("localStorage quota exceeded, clearing old data");
        try {
          localStorage.removeItem("daftarHadir");
          // Coba lagi sekali
          localStorage.setItem("daftarHadir", JSON.stringify(dataToStore));
          return true;
        } catch (retryError) {
          console.error("Failed to save after clearing:", retryError);
          return false;
        }
      }
      console.error("Error saving daftarHadir to localStorage:", error);
      return false;
    }
  };

  // Helper function untuk menentukan apakah perlu fetch dari API
  const shouldFetchFromAPI = (daftarHadir, currentDate, currentHour) => {
    if (!daftarHadir) return true;

    // Jika tanggal berbeda, perlu fetch baru
    if (daftarHadir.tanggal !== currentDate) {
      try {
        localStorage.removeItem("daftarHadir");
      } catch (e) {
        console.error("Error removing old daftarHadir:", e);
      }
      return true;
    }

    // Jika info === "bukan_hari_kerja" dan tanggal === currentDate, jangan fetch lagi sepanjang hari
    if (
      daftarHadir.info === "bukan_hari_kerja" &&
      daftarHadir.tanggal === currentDate
    ) {
      return false;
    }

    // Jika waktuMasuk belum ada, perlu fetch
    if (!daftarHadir.waktuMasuk || daftarHadir.waktuMasuk === "") {
      return true;
    }

    // Jika workingHours sudah ada, tidak perlu fetch lagi
    if (daftarHadir.workingHours && daftarHadir.workingHours !== "") {
      return false;
    }

    // Jika sudah di atas pukul 16:00 tapi waktuPulang masih kosong, perlu fetch
    if (
      currentHour >= 16 &&
      (!daftarHadir.waktuPulang || daftarHadir.waktuPulang === "")
    ) {
      return true;
    }

    // Default: tidak perlu fetch, gunakan data dari cache
    return false;
  };

  // Helper function untuk check apakah data sudah lengkap (tidak perlu interval)
  const isDataComplete = (data) => {
    return (
      data &&
      data.waktuMasuk &&
      data.waktuMasuk !== "" &&
      data.workingHours &&
      data.workingHours !== ""
    );
  };

  // Fetch attendance status
  useEffect(() => {
    if (!userId || !uid_RSS) {
      setAttendanceStatus(null);
      return;
    }

    let intervalId = null;

    const fetchAttendanceStatus = async () => {
      try {
        // Get current date in YYYY-MM-DD format
        const today = new Date();
        const currentDate = today.toISOString().split("T")[0];
        const currentHour = today.getHours();

        // Cek localStorage untuk data daftarHadir
        const daftarHadir = getStoredAttendanceData();

        // Logika pengecekan sebelum memanggil API
        const shouldFetch = shouldFetchFromAPI(
          daftarHadir,
          currentDate,
          currentHour
        );

        // Jika tidak perlu fetch, gunakan data dari localStorage
        if (!shouldFetch && daftarHadir) {
          setAttendanceStatus(daftarHadir);

          // Jika info === "bukan_hari_kerja", hentikan interval karena tidak perlu fetch lagi
          if (daftarHadir.info === "bukan_hari_kerja" && intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          // Jika data sudah lengkap, hentikan interval
          else if (isDataComplete(daftarHadir) && intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          return;
        }

        // Jika perlu fetch, panggil API
        try {
          const response = await axios.post(
            "https://smartservice.rotendaokab.go.id/api/admin/daftarHadir/cekstatus",
            {
              tanggal: currentDate,
              idUser: uid_RSS,
            },
            {
              headers: {
                "Content-Type": "application/json",
              },
              timeout: 10000, // 10 detik timeout
            }
          );

          if (response.data.success === 1 && response.data.data.length > 0) {
            const data = response.data.data[0];
            setAttendanceStatus(data);

            // Simpan ke localStorage dengan timestamp
            const dataToStore = {
              tanggal: data.tanggal || currentDate,
              waktuMasuk: data.waktuMasuk || null,
              waktuPulang: data.waktuPulang || null,
              workingHours: data.workingHours || null,
              info: data.info || null,
              hariKerjaTipe: data.hariKerjaTipe || null,
              idUser: data.idUser || null,
            };
            saveAttendanceDataToStorage(dataToStore);

            // Jika info === "bukan_hari_kerja", hentikan interval karena tidak perlu fetch lagi
            if (data.info === "bukan_hari_kerja" && intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
            // Jika data sudah lengkap (waktuMasuk dan workingHours ada), hentikan interval
            else if (isDataComplete(dataToStore) && intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
          } else {
            setAttendanceStatus(null);
            // Hapus data dari localStorage jika tidak ada data
            try {
              localStorage.removeItem("daftarHadir");
            } catch (e) {
              console.error("Error removing daftarHadir:", e);
            }
          }
        } catch (apiError) {
          // Error handling untuk API call
          if (apiError.code === "ECONNABORTED") {
            console.error("Request timeout saat fetch attendance status");
          } else if (apiError.response) {
            console.error(
              "API error:",
              apiError.response.status,
              apiError.response.data
            );
          } else if (apiError.request) {
            console.error("Network error:", apiError.request);
          } else {
            console.error(
              "Error fetching attendance status:",
              apiError.message
            );
          }

          // Jika ada data di localStorage, gunakan itu sebagai fallback
          if (daftarHadir) {
            setAttendanceStatus(daftarHadir);
          } else {
            setAttendanceStatus(null);
          }
        }
      } catch (error) {
        console.error("Unexpected error in fetchAttendanceStatus:", error);
        setAttendanceStatus(null);
      }
    };

    // Initial fetch dan setup interval setelah fetch
    const initializeAttendance = async () => {
      await fetchAttendanceStatus();

      // Setup interval hanya jika data belum lengkap dan bukan hari kerja
      const daftarHadir = getStoredAttendanceData();
      const isBukanHariKerja = daftarHadir?.info === "bukan_hari_kerja";

      if (
        !isDataComplete(daftarHadir) &&
        !isBukanHariKerja &&
        !intervalId &&
        !document.hidden
      ) {
        // Refresh every 5 minutes jika data belum lengkap dan bukan hari kerja
        intervalId = setInterval(fetchAttendanceStatus, 5 * 60 * 1000);
      }
    };

    initializeAttendance();

    // Handle visibility change untuk optimasi resource
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      const daftarHadir = getStoredAttendanceData();
      const isBukanHariKerja = daftarHadir?.info === "bukan_hari_kerja";

      if (isVisible) {
        // Window kembali aktif - fetch attendance status sekali untuk update terbaru
        // Tapi hanya jika bukan hari kerja dan data belum lengkap
        if (!isBukanHariKerja && !isDataComplete(daftarHadir)) {
          fetchAttendanceStatus();
        }

        // Resume interval jika belum ada, bukan hari kerja, data belum lengkap
        if (!intervalId && !isBukanHariKerja && !isDataComplete(daftarHadir)) {
          intervalId = setInterval(fetchAttendanceStatus, 5 * 60 * 1000);
        }
      } else {
        // Window menjadi hidden - pause interval
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    };

    // Storage event listener untuk sync antar tab
    const handleStorageChange = (e) => {
      if (e.key === "daftarHadir") {
        try {
          if (e.newValue) {
            const newData = JSON.parse(e.newValue);
            setAttendanceStatus(newData);

            // Jika info === "bukan_hari_kerja", hentikan interval
            if (newData.info === "bukan_hari_kerja") {
              if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
              }
            }
            // Jika data sudah lengkap, hentikan interval
            else if (isDataComplete(newData)) {
              if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
              }
            } else {
              // Jika data belum lengkap, bukan hari kerja, interval belum ada, dan window visible, setup interval
              if (!intervalId && !document.hidden) {
                intervalId = setInterval(fetchAttendanceStatus, 5 * 60 * 1000);
              }
            }
          } else {
            setAttendanceStatus(null);
            // Jika data dihapus dan window visible, setup interval untuk fetch ulang
            if (!intervalId && !document.hidden) {
              intervalId = setInterval(fetchAttendanceStatus, 5 * 60 * 1000);
            }
          }
        } catch (error) {
          console.error("Error parsing storage event data:", error);
        }
      }
    };

    // Listen to visibility change events
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("storage", handleStorageChange);

    // Cleanup
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [uid_RSS, userId]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);
  const handleAvatarMenuOpen = (event) => setAvatarMenu(event.currentTarget);
  const handleAvatarMenuClose = () => setAvatarMenu(null);
  const handleLiburDialogOpen = () => setLiburDialogOpen(true);
  const handleLiburDialogClose = () => setLiburDialogOpen(false);

  // Fungsi untuk flatten routes (mencari semua menu dan submenu)
  const flattenRoutes = (routesList, parentName = "") => {
    const result = [];

    routesList.forEach((route) => {
      // Skip divider, title, dan route tanpa name
      if (route.type === "divider" || route.type === "title" || !route.name) {
        return;
      }

      // Extract name string dari React element atau string
      let nameStr = "";
      if (typeof route.name === "string") {
        nameStr = route.name;
      } else if (route.name) {
        // Jika React element, coba ekstrak text dengan rekursif
        const extractText = (node) => {
          if (typeof node === "string") {
            return node;
          }
          if (Array.isArray(node)) {
            return node.map(extractText).filter(Boolean).join(" ");
          }
          if (node?.props?.children) {
            return extractText(node.props.children);
          }
          return "";
        };
        nameStr = extractText(route.name).trim();
      }

      // Jika route memiliki route path, tambahkan ke hasil
      if (route.route && nameStr) {
        result.push({
          name: nameStr,
          route: route.route,
          key: route.key,
          parentName: parentName,
          fullPath: parentName ? `${parentName} > ${nameStr}` : nameStr,
        });
      }

      // Jika route memiliki collapse, rekursif
      if (route.collapse && route.collapse.length > 0) {
        const currentParent = parentName
          ? `${parentName} > ${nameStr}`
          : nameStr;
        result.push(...flattenRoutes(route.collapse, currentParent));
      }
    });

    return result;
  };

  // Fungsi pencarian
  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query.trim().length === 0) {
      setSearchResults([]);
      setSearchOpen(false);
      setSearchAnchorEl(null);
      return;
    }

    // Flatten routes
    const flatRoutes = flattenRoutes(availableRoutes);

    // Filter berdasarkan query (case-insensitive)
    const filtered = flatRoutes.filter(
      (item) =>
        item.name.toLowerCase().includes(query.toLowerCase()) ||
        item.fullPath.toLowerCase().includes(query.toLowerCase())
    );

    setSearchResults(filtered);

    if (filtered.length > 0 && event.currentTarget) {
      setSearchAnchorEl(event.currentTarget);
      setSearchOpen(true);
    } else {
      setSearchOpen(false);
    }
  };

  // Handler klik hasil pencarian
  const handleSearchResultClick = (resultRoute) => {
    navigate(resultRoute);
    setSearchQuery("");
    setSearchResults([]);
    setSearchOpen(false);
    setSearchAnchorEl(null);
  };

  // Handler untuk menutup search menu
  const handleSearchClose = () => {
    setSearchOpen(false);
    setSearchAnchorEl(null);
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    handleCloseMenu();

    switch (notification.type) {
      case "laporan_harian":
        navigate("/daily-performance/laporan-harian/per-orang");
        break;
      case "instruksi":
        navigate("/task-management/tugas/daftar-tugas");
        break;
      case "disposisi":
        navigate("/task-management/disposisi/disposisi-TL");
        break;
      case "event":
        // Redirect ke kalender event untuk melihat event 3 hari ke depan
        navigate("/events/kalender-event");
        break;
      default:
        break;
    }
  };

  // Render the notifications menu
  const renderMenu = () => {
    // Helper function untuk mendapatkan badge config berdasarkan type
    const getBadgeConfig = (type) => {
      switch (type) {
        case "disposisi":
          return {
            label: "Disposisi",
            color: "info",
            icon: <Icon fontSize="small">description</Icon>,
          };
        case "instruksi":
          return {
            label: "Instruksi",
            color: "warning",
            icon: <Icon fontSize="small">assignment</Icon>,
          };
        case "event":
          return {
            label: "Event",
            color: "success",
            icon: <Icon fontSize="small">event</Icon>,
          };
        case "laporan_harian":
          return {
            label: "Laporan",
            color: "error",
            icon: <Icon fontSize="small">report</Icon>,
          };
        default:
          return {
            label: "Notifikasi",
            color: "default",
            icon: <Icon fontSize="small">notifications</Icon>,
          };
      }
    };

    const allNotifications = [
      ...notifications.disposisi.map((item) => ({
        ...item,
        type: "disposisi",
        message: `Disposisi baru: ${
          item.perihal || item.nomor_surat || "Tanpa judul"
        }`,
      })),
      ...notifications.instruksi.map((item) => ({
        ...item,
        type: "instruksi",
        message: `Instruksi baru: ${item.perihal || "Tanpa judul"}`,
      })),
      ...notifications.events.map((item) => ({
        ...item,
        type: "event",
        message: `Event: ${item.kegiatan}${
          item.detail_kegiatan ? ` - ${item.detail_kegiatan}` : ""
        }`,
      })),
      ...notifications.laporan_harian.map((item) => ({
        ...item,
        type: "laporan_harian",
        message: item.message || "Laporan harian belum disubmit",
      })),
    ].sort((a, b) => {
      // Urutkan berdasarkan createdAt dari terbaru ke terlama
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA; // Descending (terbaru dulu)
    });

    return (
      <Menu
        anchorEl={openMenu}
        anchorReference={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        open={Boolean(openMenu)}
        onClose={handleCloseMenu}
        sx={{ mt: 2, maxHeight: "400px", width: "350px" }}
      >
        <MDBox px={2} py={1}>
          <MDTypography variant="h6" fontWeight="medium">
            Notifikasi
          </MDTypography>
        </MDBox>
        <Divider />
        {allNotifications.length === 0 ? (
          <MDBox px={2} py={3} textAlign="center">
            <MDTypography variant="body2" color="text.secondary">
              Tidak ada notifikasi
            </MDTypography>
          </MDBox>
        ) : (
          <MDBox sx={{ maxHeight: "350px", overflowY: "auto" }}>
            {allNotifications.map((notification, index) => {
              const badgeConfig = getBadgeConfig(notification.type);
              return (
                <MDBox key={index}>
                  <MenuItem
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      cursor: "pointer",
                      width: "100%",
                      py: 1.5,
                      px: 2,
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <MDBox
                      display="flex"
                      flexDirection="column"
                      width="100%"
                      gap={0.75}
                    >
                      {/* Badge untuk kategori */}
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <Chip
                          icon={badgeConfig.icon}
                          label={badgeConfig.label}
                          color={badgeConfig.color}
                          size="small"
                          sx={{
                            height: "24px",
                            fontSize: "0.7rem",
                            fontWeight: 500,
                          }}
                        />
                        {/* Timestamp jika ada */}
                        {notification.createdAt && (
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: "auto", fontSize: "0.65rem" }}
                          >
                            {new Date(notification.createdAt).toLocaleString(
                              "id-ID",
                              {
                                day: "numeric",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </MDTypography>
                        )}
                      </MDBox>
                      {/* Message dengan word wrapping - bisa wrap ke baris kedua, ketiga, dst */}
                      <MDTypography
                        variant="body2"
                        sx={{
                          whiteSpace: "normal",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          lineHeight: 1.5,
                          width: "100%",
                          maxWidth: "100%",
                          display: "block",
                          overflow: "visible",
                          textOverflow: "clip",
                        }}
                      >
                        {notification.message}
                      </MDTypography>
                    </MDBox>
                  </MenuItem>
                  {index < allNotifications.length - 1 && <Divider />}
                </MDBox>
              );
            })}
          </MDBox>
        )}
      </Menu>
    );
  };

  // Styles for the navbar icons
  const iconsStyle = ({
    palette: { dark, white, text },
    functions: { rgba },
  }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) =>
        navbar(theme, { transparentNavbar, absolute, light, darkMode })
      }
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" sx={(theme) => navbarRow(theme, { isMini })}>
          <IconButton
            sx={navbarDesktopMenu}
            onClick={handleMiniSidenav}
            size="small"
            disableRipple
          >
            <Icon fontSize="medium" sx={iconsStyle}>
              {miniSidenav ? "menu_open" : "menu"}
            </Icon>
          </IconButton>
          {isMobile ? (
            <MDTypography
              variant="button"
              fontWeight="bold"
              textTransform="capitalize"
              color={light ? "white" : "dark"}
              sx={{ lineHeight: 0, ml: { xs: 0, xl: 1 } }}
            >
              {route[route.length - 1]?.replace("-", " ") || ""}
            </MDTypography>
          ) : (
            <Breadcrumbs
              icon="home"
              title={route[route.length - 1]}
              route={route}
              light={light}
            />
          )}
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            {/* Desktop Search Input */}
            {!isMobile && (
              <MDBox pr={1} position="relative">
                <MDInput
                  label="Search here"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={(e) => {
                    if (
                      searchResults.length > 0 &&
                      searchQuery.trim().length > 0
                    ) {
                      setSearchAnchorEl(e.currentTarget);
                      setSearchOpen(true);
                    }
                  }}
                />
                <Popper
                  open={searchOpen}
                  anchorEl={searchAnchorEl}
                  placement="bottom-start"
                  style={{ zIndex: 1300, width: searchAnchorEl?.offsetWidth }}
                >
                  <ClickAwayListener onClickAway={handleSearchClose}>
                    <Paper
                      sx={{
                        maxHeight: 400,
                        overflow: "auto",
                        mt: 1,
                        width: "100%",
                        boxShadow: 3,
                      }}
                    >
                      {searchResults.length > 0 ? (
                        searchResults.map((result, index) => (
                          <MenuItem
                            key={result.key || index}
                            onClick={() =>
                              handleSearchResultClick(result.route)
                            }
                            sx={{
                              py: 1.5,
                              px: 2,
                              "&:hover": {
                                backgroundColor: "action.hover",
                              },
                            }}
                          >
                            <MDBox>
                              <MDTypography
                                variant="button"
                                fontWeight="medium"
                              >
                                {result.name}
                              </MDTypography>
                              {result.parentName && (
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ mt: 0.5 }}
                                >
                                  {result.parentName}
                                </MDTypography>
                              )}
                            </MDBox>
                          </MenuItem>
                        ))
                      ) : searchQuery.trim().length > 0 ? (
                        <MenuItem disabled>
                          <MDTypography variant="body2" color="text.secondary">
                            Tidak ada hasil ditemukan
                          </MDTypography>
                        </MenuItem>
                      ) : null}
                    </Paper>
                  </ClickAwayListener>
                </Popper>
              </MDBox>
            )}
            {/* Mobile Search Icon */}
            {isMobile && (
              <MDBox pr={1}>
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  onClick={() => setSearchDialogOpen(true)}
                  sx={navbarIconButton}
                >
                  <Icon sx={iconsStyle}>search</Icon>
                </IconButton>
              </MDBox>
            )}
            <MDBox
              color={light ? "white" : "inherit"}
              display="flex"
              alignItems="center"
            >
              {/* Avatar user - prioritaskan dari localStorage, jika tidak ada gunakan prop */}
              {(userAvatar || avatar) && (
                <>
                  <IconButton
                    onClick={handleAvatarMenuOpen}
                    sx={{
                      p: 0,
                      mr: 1,
                      "&:hover": {
                        opacity: 0.8,
                      },
                    }}
                  >
                    <MDBox
                      component="img"
                      src={userAvatar || avatar}
                      alt="User Avatar"
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        objectFit: "cover",
                        boxShadow: 2,
                        border: "2px solid #fff",
                      }}
                      onError={(e) => {
                        // Jika gambar gagal dimuat, sembunyikan avatar
                        e.target.style.display = "none";
                      }}
                    />
                  </IconButton>
                  <Menu
                    anchorEl={avatarMenu}
                    open={Boolean(avatarMenu)}
                    onClose={handleAvatarMenuClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    {userStatus && (
                      <MenuItem disabled>
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            Status
                          </MDTypography>
                          <MDTypography
                            variant="button"
                            fontWeight="medium"
                            textTransform="uppercase"
                            color={
                              userStatus === "admin"
                                ? "success"
                                : userStatus === "super admin" ||
                                  userStatus === "pimpinan"
                                ? "warning"
                                : "info"
                            }
                          >
                            {userStatus}
                          </MDTypography>
                        </MDBox>
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
              {/* Attendance Status Display */}
              {attendanceStatus ? (
                <MDBox sx={{ mr: 1 }}>
                  {attendanceStatus.libur ? (
                    <Chip
                      label={`Libur: ${attendanceStatus.libur}`}
                      color="warning"
                      size="small"
                      onClick={handleLiburDialogOpen}
                      sx={{ cursor: "pointer" }}
                    />
                  ) : attendanceStatus.info === "bukan_hari_kerja" ? (
                    <Chip
                      label="Bukan Hari Kerja"
                      color="default"
                      size="small"
                    />
                  ) : attendanceStatus.waktuMasuk ? (
                    <Chip
                      icon={<Icon sx={{ fontSize: 16 }}>check_circle</Icon>}
                      label={
                        attendanceStatus.waktuPulang &&
                        attendanceStatus.waktuPulang !== ""
                          ? `Jam Kerja: ${attendanceStatus.workingHours || "-"}`
                          : `Masuk: ${attendanceStatus.waktuMasuk}`
                      }
                      color="success"
                      size="small"
                    />
                  ) : null}
                </MDBox>
              ) : (
                <MDBox sx={{ mr: 1 }}>
                  <MDBadge
                    badgeContent="Belum check-in"
                    color="error"
                    size="small"
                  />
                </MDBox>
              )}
              {!isMobile && userStatus ? (
                <MDBadge
                  badgeContent={userStatus}
                  color={
                    userStatus === "admin"
                      ? "success"
                      : userStatus === "super admin" ||
                        userStatus === "pimpinan"
                      ? "warning"
                      : "info"
                  }
                  size="xs"
                  container
                  sx={{ mr: 1, textTransform: "uppercase" }}
                />
              ) : null}
              <IconButton
                sx={navbarIconButton}
                size="small"
                disableRipple
                onClick={() => logoutUser(navigate)}
              >
                <Icon sx={iconsStyle}>logout</Icon>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              {!isMobile && (
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  onClick={handleConfiguratorOpen}
                >
                  <Icon sx={iconsStyle}>settings</Icon>
                </IconButton>
              )}
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <MDBadge
                  badgeContent={
                    notifications.total > 0 ? notifications.total : null
                  }
                  color="error"
                  size="xs"
                  circular
                >
                  <Icon sx={iconsStyle}>notifications</Icon>
                </MDBadge>
              </IconButton>
              {renderMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
      {/* Mobile Search Dialog */}
      <Dialog
        open={searchDialogOpen}
        onClose={() => {
          setSearchDialogOpen(false);
          setSearchQuery("");
          setSearchResults([]);
          setSearchOpen(false);
          setSearchAnchorEl(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Cari Menu</MDTypography>
            <IconButton
              onClick={() => {
                setSearchDialogOpen(false);
                setSearchQuery("");
                setSearchResults([]);
                setSearchOpen(false);
                setSearchAnchorEl(null);
              }}
              size="small"
            >
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2}>
            <MDInput
              label="Search here"
              value={searchQuery}
              onChange={handleSearchChange}
              fullWidth
              autoFocus
            />
            {searchResults.length > 0 && (
              <MDBox mt={2} sx={{ maxHeight: 400, overflow: "auto" }}>
                {searchResults.map((result, index) => (
                  <MenuItem
                    key={result.key || index}
                    onClick={() => {
                      handleSearchResultClick(result.route);
                      setSearchDialogOpen(false);
                    }}
                    sx={{
                      py: 1.5,
                      px: 2,
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <MDBox>
                      <MDTypography variant="button" fontWeight="medium">
                        {result.name}
                      </MDTypography>
                      {result.parentName && (
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          {result.parentName}
                        </MDTypography>
                      )}
                    </MDBox>
                  </MenuItem>
                ))}
              </MDBox>
            )}
            {searchQuery.trim().length > 0 && searchResults.length === 0 && (
              <MDBox mt={2} textAlign="center" py={3}>
                <MDTypography variant="body2" color="text.secondary">
                  Tidak ada hasil ditemukan
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
      </Dialog>
    </AppBar>
  );
}

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  avatar: PropTypes.string,
};

export default DashboardNavbar;
